<template>
	<section
		class="Card"
		ref="cardRef"
		@click="$emit('click')"
	>
		<slot></slot>
		<div :class="['Footer', isFeatured || loading ? 'with-featured' : '']">
			<span
				v-if="isFeatured || loading"
				v-skeleton="{
					loading,
					sizes: {
						Small: { width: '3.188rem', height: '0.75rem' },
					}
				}"
				class="Featured"
			>
				Featured
			</span>
			<slot name="right-footer"></slot>
		</div>
	</section>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue';

const props = defineProps({
	featured: {
		type: Boolean,
		default: false,
	},
	loading: {
		type: Boolean,
		default: false,
	},
});

const emits = defineEmits([
	'click',
	'visible',
]);

const isFeatured = computed(() => props.featured);

const observer = ref<IntersectionObserver | null>(null);
const cardRef = ref<HTMLElement | null>(null);
const hasEmittedVisible = ref(false);

onMounted(() => {
	observer.value = new IntersectionObserver(handleIntersection,{ threshold: 0.5 });

	if (!cardRef.value) return;

	observer.value.observe(cardRef.value);
});

const handleIntersection: IntersectionObserverCallback = (entries) => {
	entries.forEach((entry) => {
		if (!entry.isIntersecting || hasEmittedVisible.value) return;

		hasEmittedVisible.value = true;
		emits('visible');

		if (!observer.value || !cardRef.value) return;

		observer.value.unobserve(cardRef.value);
	});
};

onUnmounted(() => {
	if (!observer.value || !cardRef.value) return;

	observer.value.unobserve(cardRef.value);
	observer.value.disconnect();
});
</script>

<style scoped lang="scss">
	.Card {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: flex-start;
		box-sizing: border-box;

		width: 100%;

		border-radius: 0.5rem;
		background-color: $White;
		box-shadow: $BoxShadow;

		@include PaddingAreas(0.5, 1, 1, 1);

		.Footer {
			display: flex;
			justify-content: end;
			align-items: center;
			width: 100%;

			@include MarginAreas(0.5, 0, 0, 0);

			&.with-featured {
				justify-content: space-between;
			}

			.Featured {
				color: $Grey-700;

				@include TextStyle(UiSmallRegular);
			}
		}

		@include Breakpoint(Large, ExtraLarge) {
			@include PaddingAreas(1);
		}
	}

</style>
